import { CheckCircleOutline, PriorityHigh } from '@mui/icons-material';
import {
  Button,
  Container,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type { AxiosError } from 'axios';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { useGlobalData } from '@/contexts/GlobalDataProvider';
import { useProgress } from '@/contexts/ProgressProvider';
import VnModalBody from '@/modals/components/VnModalBody';
import VnModalFooter from '@/modals/components/VnModalFooter';
import { VNBaseModal } from '@/modals/VNBaseModal';
import { useModalContext } from '@/modals/VNModalProvider';
import { applicationDataSelector } from '@/store/slices/application-data';
import itemUtils from '@/utils/itemUtils';

import FuelAutoComplete from '../../../components/fuel-autocomplete';
import FuelTags from '../../../components/fuel-tags';
import VNAutocomplete from '../../../components/VNAutocomplete/VNAutocomplete';
import type { DropdownOption } from '../../../components/VNDropdown';
import type { Category } from '../../../models';
import type { FuelMapperTemplate } from '../../../models/fuel-mapper-template';
import { PaginationMethod } from '../../../models/Ou';
import type { ValidationError } from '../../../models/ValidationError';
import { ValidationErrors } from '../../../models/ValidationErrors';
import { FeedIngestService } from '../../../services/feed-ingest-service';
import { baseAxios } from '../../../services/interceptor';
import type { FeedConfigForm } from '../../../store/forms/Admin/FeedConfigForm';
import useAppUtils from '../../../utils/appUtils';
import clientUtils from '../../../utils/clientUtils';
import type { FeedValidationModel } from './FeedValidationResultModal';

const FIND_THRESHOLD = 140;

type FeedConfigModalFormValues = FeedConfigForm & {
  categoryIds: string[];
};

interface IPros {
  onSave: (data: FeedConfigForm) => void;
  form: FeedConfigForm;
  isExisting: boolean;
}

const FeedValidationResultModal = ({ open, onClose, form, inputData, sourceXML }) => {
  const { confirm, alert } = useAppUtils();
  const [data, setData] = useState(inputData);

  useEffect(() => {
    setData(inputData);
  }, [inputData]);

  const capitalizeFirstLetter = (str) => {
    let newStr = str;
    newStr = newStr[0].toUpperCase() + newStr.substring(1);
    return newStr;
  };

  const fieldClicked = (data, key) => {
    let values: string[] = [];
    let value: any = null;

    let defaultStart = -1;
    const defaultEnd = -1;
    let innerHTML = null;
    const textarea: any = document.getElementById('feed-validation-result-textarea');
    // console.log('textarea', textarea);
    if (textarea) {
      innerHTML = textarea.innerHTML;
      // console.log('textarea textcontent', { innerHTML, innerText });
      innerHTML = innerHTML
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;amp;/g, '&amp;') // replace double replace ampersands with single
        .replace(/&amp;#/g, '&#'); // replace with xml proper varation
    }

    switch (key) {
      case 'id':
        values.push(data.extractedId);
        break;
      case 'title':
        values.push(data.extractedTitle);
        break;
      case 'description':
        values.push(data.extractedDescription);
        break;
      case 'publicationDate':
        value = data.extractedPublicationDate;
        // console.log('extractedPublicationDate', { value });
        const luxonDate = DateTime.fromJSDate(new Date(value));
        // console.log('luxonDate', { luxonDate });
        values.push(`${luxonDate.year}`);
        values.push(`${luxonDate.month}`);
        values.push(`${luxonDate.monthLong}`);
        values.push(`${luxonDate.monthShort}`);
        values.push(`${luxonDate.day}`);
        values.push(`${luxonDate.hour}`);
        // for pub date the best we can do is search through the date parts for year
        break;
      case 'subtitles':
        value = data.extractedSubtitles;
        if (value != null) {
          Object.keys(value).map((key, index) => {
            values.push(value[key]);
          });
        }
        break;
      case 'thumbnail':
        values.push(data.extractedThumbnail);
        break;
      case 'sourceUrl':
        values.push(data.extractedSourceUrl);
        break;
      case 'categories':
        value = data.extractedCategories;
        if (value != null && value.length > 0) {
          values = value;
        }
        break;
      case 'tags':
        value = data.extractedTags;
        const newStart = innerHTML.indexOf('keywords');
        if (newStart > -1) {
          defaultStart = newStart;
        }
        // console.log('tags value', { value });
        if (value != null && value.length > 0) {
          values = value;
        }
        break;
    }

    values = filterValues(values);
    // console.log('filtered values', { values });
    if (values.length > 0) {
      const textarea: any = document.getElementById('feed-validation-result-textarea');
      // console.log('textarea', textarea);
      let start = -1;
      let end = -1;
      if (innerHTML != null) {
        const results = getSelectionRange(innerHTML, values, defaultStart);
        start = results.start;
        end = results.end;
      }
      if (start != -1 && end != -1) {
        setSelectionRange(textarea, start, end);
        // textarea.focus();
        // textarea.setSelectionRange(start, end);
      }
    }
  };

  const filterValues = (values) => {
    if (values == null) return null;
    values = _.filter(values, (x) => x != null);
    for (let i = 0; i < values.length; i++) {
      values[i] = values[i].replace(/'/g, '&#39;');
    }
    return values;
  };

  const getSelectionRange = (innerHTML: string, values: string[], defaultStart: number = -1) => {
    // console.log('getSelectionRange', { innerHTML, values, defaultStart });
    if (defaultStart > -1) {
      innerHTML = innerHTML.substring(defaultStart);
    }
    let start = -1;
    let end = -1;
    for (let i = 0; i < values.length; i++) {
      const value = values[i];
      const start2 = innerHTML.indexOf(value);
      let end2 = -1;
      if (start2 > -1) {
        if (start == -1) {
          start = start2;
          end2 = start2 + value.length;
        } else if (Math.abs(start2 - start) < FIND_THRESHOLD || Math.abs(start2 - end) < FIND_THRESHOLD) {
          start = Math.min(start, start2);
          end2 = start2 + value.length;
        }
      }
      if (end2 > -1) {
        if (end == -1) {
          end = end2;
        } else if (Math.abs(end2 - end) < FIND_THRESHOLD || Math.abs(end2 - start) < FIND_THRESHOLD) {
          end = Math.max(end, end2);
        }
      }
    }
    if (defaultStart > -1) {
      start += defaultStart;
      end += defaultStart;
    }
    return { start, end };
  };

  const setSelectionRange = (textarea, selectionStart, selectionEnd) => {
    // First scroll selection region to view
    const fullText = textarea.value;
    textarea.value = fullText.substring(0, selectionEnd);
    // For some unknown reason, you must store the scollHeight to a variable
    // before setting the textarea value. Otherwise it won't work for long strings
    const { scrollHeight } = textarea;
    textarea.value = fullText;
    let scrollTop = scrollHeight;
    const textareaHeight = textarea.clientHeight;
    if (scrollTop > textareaHeight) {
      // scroll selection to center of textarea
      scrollTop -= textareaHeight / 2;
    } else {
      scrollTop = 0;
    }

    // Continue to set selection range
    textarea.focus();
    textarea.setSelectionRange(selectionStart, selectionEnd);

    setTimeout(() => {
      textarea.scrollTop = scrollTop;
    }, 0);
  };

  const validateEntireFeed = async () => {
    // console.log('validateEntireFeed', { form });
    const url = form.feedUrl;
    try {
      const response = await FeedIngestService.validateFeed(url, true).catch((axiosError: AxiosError) => {
        const message: string = axiosError.response?.data || 'Unable to validate';
        alert(message, 'warning');
      });

      if (!response) {
        return; // early return if response is null
      }

      if (response && response.data && response.status === 200) {
        setData(response.data);
      } else {
        alert('Unable to validate', 'error');
        console.error('Unable to validate the source due to', response);
      }
    } catch (ex) {
      alert('Unable to validate', 'error');
      console.error('Unable to validate the source due to', ex);
    }
  };

  return (
    <VNBaseModal open={open} width="lg" title="Feed Validation" onClose={onClose} cssClassName="feed-validation-result-modal">
      <VnModalBody>
        <div className="feed-validation-feilds-container">
          <List>
            {/* 👇️ iterate object KEYS */}
            {data != null &&
              Object.keys(data).map((key, index) => {
                return (
                  <React.Fragment key={index}>
                    {key.substring(0, 9) !== 'extracted' && (
                      <ListItem className="clickable" onClick={() => fieldClicked(data, key)}>
                        <ListItemText>{capitalizeFirstLetter(key)}</ListItemText>
                        <ListItemIcon>
                          {data[key] ? (
                            <CheckCircleOutline sx={{ color: '#78b13f', marginLeft: 1 }} />
                          ) : (
                            <PriorityHigh sx={{ color: '#fd3', marginLeft: 1 }} />
                          )}
                        </ListItemIcon>
                      </ListItem>
                    )}
                  </React.Fragment>
                );
              })}
          </List>
        </div>
        <div className="feed-validation-textarea-container">
          <TextField variant="outlined" fullWidth placeholder="Add Template" multiline minRows={8} value={sourceXML} />

          {/* <textarea id="feed-validation-result-textarea" rows="1" className="result-textarea" value={sourceXML} readOnly /> */}
        </div>
      </VnModalBody>
      <VnModalFooter
        right={
          <>
            <Button color="primary" variant="contained" onClick={validateEntireFeed}>
              Validate Entire Feed
            </Button>
            <Button autoFocus variant="contained" onClick={onClose}>
              Close
            </Button>
          </>
        }
      />
    </VNBaseModal>
  );
};

const FeedConfigModal = ({ form, isExisting, onSave }: IPros) => {
  const methods = useForm<FeedConfigModalFormValues>({
    defaultValues: useMemo(
      () => ({
        ...form,
        categoryIds: form.selectedCategories?.map((c) => c.id) ?? [],
      }),
      [form]
    ),
  });
  const { register, handleSubmit, control, setValue, getValues, reset, formState } = methods;
  const { ouData } = useSelector(applicationDataSelector);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [allowMultiCategorySelect, setAllowMultiCategorySelect] = useState(true);

  // const { categories } = useSelector(applicationDataSelector);
  const { onClose } = useModalContext();
  const { confirm, alert } = useAppUtils();
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [selectedTags, setSelectedTags] = useState(form.defaultTags);
  const [selectedMethod, setSelectedMethod] = useState<any>(form.selectedMethod);
  const [enabled, setEnabled] = useState(form.enabled);
  const [extractExternalServiceMeta, setExtractExternalServiceMeta] = useState(form.extractExternalServiceMeta);
  const [errors, setErrors] = useState([]);
  const [validationResult, setValidationResult] = useState<FeedValidationModel>();
  const [validationSourceXML, setValidationSourceXML] = useState('');
  const [openFeedValidationModal, setOpenFeedValidationModal] = useState(false);
  const [validateButtonDisabled, setValidateButtonDisabled] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<FuelMapperTemplate>(form.selectedTemplate);
  const [showTemplate, setShowTemplate] = useState(false);
  const [radioValue, setRadioValue] = useState(form.selectedTemplate ? 'mapper' : 'url');
  const { globalData } = useGlobalData();
  const { loading, setProgress } = useProgress();
  const modal = useModalContext();

  const categoryOptions = useMemo<DropdownOption[]>(
    () => (filteredCategories ?? []).map((c: Category) => ({ id: c.id, label: c.name })),
    [filteredCategories]
  );

  const syncSelectedCategoriesFromIds = (ids: string[]) => {
    const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
    const selected = uniqueIds
      .map((id) => filteredCategories.find((c: Category) => c.id === id) ?? ouData.categories?.find((c) => c.id === id))
      .filter((c): c is Category => Boolean(c));
    setSelectedCategories(selected);
  };

  useEffect(() => {
    // console.log('FeedConfigModal init', { form });
    // Set allowMultipleCategories
    const allowMultipleCategories = ouData?.metaData?.allowMultipleCategories !== false;
    const itemCategories = form.selectedCategories;
    if (allowMultipleCategories === true || itemCategories?.length > 1) {
      setAllowMultiCategorySelect(true);
    } else {
      setAllowMultiCategorySelect(false);
    }

    // Set categories
    const myFilteredCategories = itemUtils.getFilteredCategories(ouData.categories, itemCategories);
    setFilteredCategories(myFilteredCategories);

    const mySelectedCategories =
      myFilteredCategories.filter(
        (x) => itemCategories?.map((c: Category) => c.id).findIndex((id: string) => id === x.id) > -1
      ) ?? [];
    setSelectedCategories(mySelectedCategories);
    setValue(
      'categoryIds',
      mySelectedCategories.map((c) => c.id),
      { shouldValidate: false }
    );
  }, [ouData, form.selectedCategories, setValue]);

  const handleTagsChange = (tags) => {
    setSelectedTags(tags);
  };
  const handleClose = () => {
    // setOpen(false);
    onClose();
  };

  const handleToggleChange = (e, field) => {
    // console.log({e, field})
    if (field === 'enabled') {
      setValue('enabled', e.target.checked);
      setEnabled(e.target.checked);
    } else if (field === 'extractExternalServiceMeta') {
      setValue('extractExternalServiceMeta', e.target.checked);
      setExtractExternalServiceMeta(e.target.checked);
    }
  };
  const submit: SubmitHandler<FeedConfigForm> = async (data: FeedConfigForm) => {
    const isValid = validateForm(data);
    setErrors(isValid);
    if (isValid.errors && isValid.errors.length > 0) {
      const message = `Please fix the errors before saving: ${isValid.errors[0].message}`;
      alert(message, 'warning');
      return;
    }
    if (selectedMethod && selectedMethod.text === PaginationMethod.None) {
      data.paginationConfig = null;
    } else if (selectedMethod && selectedMethod.text !== PaginationMethod.None) {
      data.paginationConfig = {
        parameterName: data.parameterName,
        initialValue: +data.initialValue,
        method: selectedMethod.text,
        pageLimit: +data.pageLimit,
      };
    }
    data.defaultCategories = selectedCategories.map((c) => c.name);
    data.defaultTags = selectedTags;
    onSave(data);
  };

  const validateForm = (form: FeedConfigForm) => {
    const errors: Array<ValidationError> = [];

    if (form.feedName == null || form.feedName.length <= 0) {
      errors.push({ fieldId: 'feedName', message: 'Feed Name is required.' });
    }
    if (radioValue === 'url') {
      if (form.feedUrl == null || form.feedUrl?.trim()?.length === 0) {
        errors.push({ fieldId: 'feedUrl', message: 'Feed Url is required.' });
      }
      if (!clientUtils.isValidUrl(form.feedUrl)) {
        errors.push({ fieldId: 'feedUrl', message: 'Feed Url must be a valid URL.' });
      }
    }
    if (radioValue === 'mapper' && form.selectedTemplate === '') {
      errors.push({ fieldId: 'selectedTemplate', message: 'Mapper Template is required.' });
    }
    if (selectedMethod.text === PaginationMethod.TemplatedFeedUrl || selectedMethod.text === PaginationMethod.QueryParam) {
      if (form.initialValue == null) {
        errors.push({ fieldId: 'initialValue', message: 'Initial Value is required.' });
      }
      if (form.pageLimit == null) {
        errors.push({ fieldId: 'pageLimit', message: 'Page limit is required' });
      }
    }
    if (
      selectedMethod.text === PaginationMethod.QueryParam ||
      selectedMethod.text === PaginationMethod.SyndicationChannelElementLink
    ) {
      if (form.parameterName.trim().length === 0) {
        errors.push({ fieldId: 'parameterName', message: 'Parameter Name is required.' });
      }
    }
    if (form.pageLimit < 0 || form.pageLimit > 1000) {
      errors.push({ fieldId: 'pageLimit', message: 'Page limit should be more than 0 and not over 1000' });
    }
    return new ValidationErrors(errors);
  };

  const handleMethodChange = (value) => {
    // console.log('selectedMethod', value);
    setSelectedMethod(value);
  };
  const handleTemplateChange = (value) => {
    setSelectedTemplate(value);
    const url = `${globalData.fuelMapperServiceUrl}map/${value.id}?fisfl=true`;
    setValue('feedUrl', url);
    setValue('selectedTemplate', value);
  };

  const validateSource = async () => {
    // console.log('validateSource', { form });
    const url = getValues('feedUrl');
    const sourceXmlUrl = '';
    try {
      setProgress(true);
      const response = await FeedIngestService.validateFeed(url).catch((axiosError: AxiosError) => {
        // console.log('here', { axiosError });
        const message: string = axiosError.response?.data || 'Unable to validate';
        alert(message, 'warning');
      });

      if (!response) {
        return; // early return if response is null
      }

      if (response && response.data && response.status === 200) {
        try {
          const sourceUrl =
            form && form.selectedTemplate && form.selectedTemplate.dataSourceUrl
              ? form.selectedTemplate.dataSourceUrl
              : form.feedUrl;
          const sourceXML = await baseAxios.get(sourceUrl);
          if (sourceXML && sourceXML.data && sourceXML.status == 200) {
            setValidationSourceXML(sourceXML.data);
          } else {
            setValidationSourceXML('');
          }
        } catch {}
        setValidationResult(response.data);
        setOpenFeedValidationModal(true);
        // setOpenFeedValidationModal(true);
      } else {
        alert('Unable to validate', 'error');
        console.error('Unable to validate the source due to', response);
      }
    } catch (ex) {
      alert('Unable to validate', 'error');
      console.error('Unable to validate the source due to', ex);
    } finally {
      setProgress(false);
    }
  };

  const onFeedUrlBlur = () => {
    if (clientUtils.isNullOrWhitespace(getValues('feedUrl'))) {
      setValidateButtonDisabled(true);
    } else {
      setValidateButtonDisabled(false);
    }
  };

  const handleRadioButtonChange = (value) => {
    setRadioValue(event.target.value);
  };

  useEffect(() => {
    onFeedUrlBlur();
  }, [getValues('feedUrl')]);

  useEffect(() => {
    // console.log('selectedMethod', selectedMethod);
  }, [form]);
  useEffect(() => {
    // console.log('selectedMethod', selectedMethod);
  }, [selectedMethod]);
  useEffect(() => {}, [errors]);
  return (
    <div>
      <FormProvider {...methods}>
        <VnModalBody>
          <Container className="no-padding" section="component" maxWidth="lg">
            <Grid container className="section-box" spacing={2}>
              <Grid item xs={6}>
                <TextField
                  variant="outlined"
                  label="Feed Name"
                  fullWidth
                  size="small"
                  required
                  error={!!formState.errors.feedName}
                  {...register('feedName', { required: true })}
                />
                <Typography variant="inherit" className="text-danger">
                  {formState.errors.feedName && <span>Feed Name is required</span>}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <RadioGroup
                  aria-labelledby="demo-controlled-radio-buttons-group"
                  name="controlled-radio-buttons-group"
                  value={radioValue}
                  onChange={handleRadioButtonChange}
                  row
                >
                  <FormControlLabel value="url" control={<Radio size="small" />} label="Url" />
                  <FormControlLabel value="mapper" control={<Radio size="small" />} label="Mapper" />
                </RadioGroup>
              </Grid>
              <Grid item xs={12}>
                {radioValue === 'mapper' && (
                  <FuelAutoComplete
                    id="mapper-templates"
                    items={getValues('templates')}
                    itemText="templateName"
                    labelText="Template"
                    placeHolderText="Select Template"
                    controlWidth={350}
                    multiSelect={false}
                    labelPaddingTop="-5px"
                    {...register('selectedTemplate', { required: true })}
                    onChange={handleTemplateChange}
                    itemKey="id"
                    defaultValue={selectedTemplate ?? ''}
                    isRequired
                    hasError={!!formState.errors.selectedTemplate}
                  />
                )}
                {radioValue === 'mapper' && (
                  <Typography variant="inherit" className="text-danger">
                    {formState.errors.selectedTemplate && <span>Mapper Template is required</span>}
                  </Typography>
                )}
              </Grid>
              {radioValue === 'url' && (
                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    label="Feed Url"
                    fullWidth
                    size="small"
                    required
                    error={!!formState.errors.feedUrl}
                    {...register('feedUrl', { required: true })}
                    onBlur={onFeedUrlBlur}
                  />
                  <Typography variant="inherit" className="text-danger">
                    {formState.errors.feedUrl && <span>Feed Url is required</span>}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={6}>
                <VNAutocomplete
                  label="Categories"
                  name="categoryIds"
                  options={categoryOptions}
                  valueProp="id"
                  labelProp="label"
                  multiple={allowMultiCategorySelect}
                  allowClear
                  chipClassName="tag-chip cat"
                  limitTags={1}
                  searchable="inset"
                  inputProps={{ placeholder: 'Select Categories' }}
                  onChange={(newValue) => {
                    let ids: string[];
                    if (allowMultiCategorySelect) {
                      ids = itemUtils.ensureArray(newValue) as string[];
                    } else if (newValue) {
                      ids = [String(newValue)];
                    } else {
                      ids = [];
                    }
                    setValue('categoryIds', ids, { shouldValidate: false });
                    syncSelectedCategoriesFromIds(ids);
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <FuelTags
                  id="fuel-ingest-tag-id"
                  tags={selectedTags}
                  inputTagWidth={250}
                  onChange={handleTagsChange}
                  allowClear
                />
              </Grid>
              <Grid item xs={6}>
                <FuelAutoComplete
                  id="feed-configs-methods"
                  items={form.methods}
                  itemText="name"
                  itemKey="id"
                  labelText="Method"
                  placeHolderText="Select Method"
                  multiSelect={false}
                  labelPaddingTop="-5px"
                  onChange={handleMethodChange}
                  defaultValue={selectedMethod}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  variant="outlined"
                  label="Parameter Name"
                  fullWidth
                  size="small"
                  disabled={selectedMethod.text === 'None' || selectedMethod.text === 'TemplatedFeedUrl'}
                  required
                  defaultValue={getValues('parameterName')}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setValue('parameterName', event.target.value);
                  }}
                  error={errors && errors.has && errors.has('parameterName')}
                />
                {errors && errors.has && errors.has('parameterName') && (
                  <Typography variant="inherit" className="text-danger" test-id="ingest-title">
                    <span>{errors.message('parameterName')}</span>
                  </Typography>
                )}
              </Grid>
              <Grid item xs={6}>
                <TextField
                  variant="outlined"
                  label="Initial Value"
                  fullWidth
                  size="small"
                  disabled={selectedMethod.text !== 'QueryParam' && selectedMethod.text !== 'TemplatedFeedUrl'}
                  required
                  defaultValue={getValues('initialValue')}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setValue('initialValue', clientUtils.parseNumber(event.target.value));
                  }}
                  onKeyPress={(event) => clientUtils.keypress(event)}
                  onPaste={clientUtils.onPaste}
                  error={errors && errors.has && errors.has('initialValue')}
                />
                {errors && errors.has && errors.has('initialValue') && (
                  <Typography variant="inherit" className="text-danger" test-id="ingest-title">
                    <span>{errors.message('initialValue')}</span>
                  </Typography>
                )}
              </Grid>
              <Grid item xs={6}>
                <TextField
                  variant="outlined"
                  label="Page Limit"
                  fullWidth
                  size="small"
                  defaultValue={getValues('pageLimit')}
                  disabled={selectedMethod.text === PaginationMethod.None}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setValue('pageLimit', clientUtils.parseNumber(event.target.value));
                  }}
                  error={errors && errors.has && errors.has('pageLimit')}
                  onKeyPress={(event) => clientUtils.keypress(event)}
                  onPaste={clientUtils.onPaste}
                />
                {errors && errors.has && errors.has('pageLimit') && (
                  <Typography variant="inherit" className="text-danger" test-id="ingest-title">
                    <span>{errors.message('pageLimit')}</span>
                  </Typography>
                )}
              </Grid>
              <Grid item xs={3}>
                <FormControlLabel
                  control={
                    <Switch
                      className="fuel-toggle"
                      sx={{ marginTop: `-1px`, marginLeft: `0px` }}
                      inputProps={{ 'aria-label': '' }}
                      checked={enabled}
                      onChange={(e) => handleToggleChange(e, 'enabled')}
                      size="small"
                    />
                  }
                  label="Enabled"
                  labelPlacement="start"
                  sx={{ marginLeft: 0 }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      className="fuel-toggle"
                      sx={{ marginTop: `-1px`, marginLeft: `0px` }}
                      inputProps={{ 'aria-label': '' }}
                      checked={extractExternalServiceMeta}
                      onChange={(e) => handleToggleChange(e, 'extractExternalServiceMeta')}
                      size="small"
                    />
                  }
                  label="Extract JWPlayer Reference IDs"
                  labelPlacement="start"
                  sx={{ marginLeft: 0 }}
                />
              </Grid>
            </Grid>
          </Container>
        </VnModalBody>
      </FormProvider>
      {openFeedValidationModal && (
        <FeedValidationResultModal
          open={openFeedValidationModal}
          onClose={() => setOpenFeedValidationModal(false)}
          form={form}
          inputData={validationResult}
          sourceXML={validationSourceXML}
        />
      )}
      <VnModalFooter
        right={
          <>
            {' '}
            <Button autoFocus color="primary" variant="contained" disabled={validateButtonDisabled} onClick={validateSource}>
              Validate Source
            </Button>
            <Button color="primary" variant="contained" disabled={loading} autoFocus onClick={handleSubmit(submit)}>
              {isExisting ? 'Update' : 'Create'}
            </Button>
            <Button autoFocus variant="contained" className="cancel-button" onClick={handleClose}>
              Cancel
            </Button>
          </>
        }
      />
    </div>
  );
};

export default FeedConfigModal;
